/**
 * Stop-speaking control for the Companion (SDK 2.11.0, 5 Sep 2026).
 *
 * Shown only while the avatar is talking. Two placements share one
 * component: beside the chest mic inside the face pane when the panel is
 * open, and beside the mini mic above the launcher when minimized —
 * always near the face, never over it (same rule as the mic controls).
 * Keyboard-reachable like any button; `aria-label="Stop speaking"`.
 */

import React from 'react';
import { CAMLIN_COMPANION_ATTR } from './attributes';
import { COMPANION_CHEST_MIC_SIZE_PX } from './CompanionChestMic';
import { COMPANION_STOP_SPEAKING_LABEL } from './speakingControl';

export function StopIcon({ size = 16 }: { size?: number }): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

const STOP_BASE_STYLE: React.CSSProperties = {
  borderRadius: '999px',
  border: 'none',
  background: '#dc2626',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 6px 20px rgba(2,6,23,0.45)',
  padding: 0,
  pointerEvents: 'auto'
};

/** Sits to the right of the chest mic, inside the face pane. */
export function buildChestStopStyle(): React.CSSProperties {
  const size = Math.round(COMPANION_CHEST_MIC_SIZE_PX * 0.8);
  return {
    ...STOP_BASE_STYLE,
    position: 'absolute',
    left: `calc(50% + ${Math.round(COMPANION_CHEST_MIC_SIZE_PX / 2) + 12}px)`,
    bottom: `${12 + Math.round((COMPANION_CHEST_MIC_SIZE_PX - size) / 2)}px`,
    width: `${size}px`,
    height: `${size}px`,
    zIndex: 26
  };
}

export interface CompanionStopButtonProps {
  /** Where the button lives: face pane (panel open) or beside the launcher. */
  placement: 'chest' | 'launcher';
  style?: React.CSSProperties;
  onClick: () => void;
}

export function CompanionStopButton({ placement, style, onClick }: CompanionStopButtonProps): React.ReactElement {
  const attr = placement === 'chest'
    ? { [CAMLIN_COMPANION_ATTR.panel]: 'stop-speaking' }
    : { [CAMLIN_COMPANION_ATTR.badge]: 'stop-speaking' };
  return (
    <button
      type="button"
      {...attr}
      style={style ?? (placement === 'chest' ? buildChestStopStyle() : STOP_BASE_STYLE)}
      aria-label={COMPANION_STOP_SPEAKING_LABEL}
      title={COMPANION_STOP_SPEAKING_LABEL}
      onClick={onClick}
    >
      <StopIcon size={placement === 'chest' ? 20 : 15} />
    </button>
  );
}

export default CompanionStopButton;
